import { Offer } from "@/services/backend/qpm/offers/types/offer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useQpmOfferSetContractTermsMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { createSetContractTermsRequestFromOffer } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/contract-terms-request";
import {
  resolvedPriceAvailability,
  resolvedPriceVisibility,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";

export type MaintenanceTableComponentProps = {
  offer: Offer;
};

export function MaintenanceTableComponent({
  offer,
}: MaintenanceTableComponentProps) {
  const [setContractTerms, { isLoading: isUpdatingContractTerms }] =
    useQpmOfferSetContractTermsMutation();
  const disabled = offer.immutable;
  const visibility = resolvedPriceVisibility(offer);
  const availability = resolvedPriceAvailability(offer);
  const includeIoTInQpm = offer.includeIoTCheckpointServiceInQpm ?? false;
  const qpmCohorts = offer.maintenanceResult.qpmCohorts ?? [];
  const qpmRows =
    qpmCohorts.length > 0 ? qpmCohorts : [offer.maintenanceResult.qpm];
  const hiddenBySettingText = t(
    "Diese Position ist im PDF ausgeblendet, da sie in den Preispositionen deaktiviert wurde.",
  );

  const onIncludeIoTInQpmChanged = (checked: boolean) => {
    const request = {
      ...createSetContractTermsRequestFromOffer(offer),
      includeIoTCheckpointServiceInQpm: checked,
    };
    setContractTerms(request);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-start gap-2">
        <Checkbox
          checked={includeIoTInQpm}
          disabled={disabled || isUpdatingContractTerms}
          onCheckedChange={(checked) => {
            onIncludeIoTInQpmChanged(checked === true);
          }}
        />
        <span className="text-sm">
          {t("IoT QPM Wartung in Service QPM Monitore einrechnen")}
        </span>
        <InfoIconTooltip
          text={t(
            "Dies ist zum Beispiel der Fall, wenn der Kunde keinen SbD Vertrag vorliegen hat und der Service noch nicht im Bestandsvertrag berechnet wurde. Das aktivieren dieses Feldes bedeutet, dass der Preis zusätzlich im QPM Service Angebot berücksichtigt und aufgerechnet wird.",
          )}
        />
      </div>
      <Table className="min-w-[760px]">
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="h-10 px-2 py-2 font-semibold">
              {t("Position")}
            </TableHead>
            <TableHead className="h-10 px-2 py-2 text-end font-semibold">
              {t("Maßnahmen")}
            </TableHead>
            <TableHead className="h-10 px-2 py-2 text-end font-semibold">
              {t("Preis pro Maßnahme")}
            </TableHead>
            <TableHead className="h-10 px-2 py-2 text-end font-semibold">
              {t("Gesamtpreis")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {availability.maintenance.qpm &&
            qpmRows.map((row, index) => (
              <TableRow
                className={
                  visibility.maintenance.qpm
                    ? "border-none"
                    : "border-none opacity-50"
                }
                key={`qpm-${index}-${row.amountOfMeasures}`}
              >
                <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
                  <div className="flex items-center gap-2">
                    <span>{t("Service QPM Monitore")}</span>
                    {!visibility.maintenance.qpm ? (
                      <InfoIconTooltip text={hiddenBySettingText} />
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <IntNumberInput
                    className="ml-auto w-20"
                    value={row.amountOfMeasures}
                    disabled={disabled || !visibility.maintenance.qpm}
                  />
                </TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <FloatNumberInput
                    className="ml-auto w-32"
                    value={row.costPerMeasure}
                    disabled={disabled || !visibility.maintenance.qpm}
                  />
                </TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <FloatNumberInput
                    className="ml-auto w-32"
                    value={row.totalCost}
                    disabled={disabled || !visibility.maintenance.qpm}
                  />
                </TableCell>
              </TableRow>
            ))}
          {availability.maintenance.aLoRa && (
            <TableRow
              className={
                visibility.maintenance.aLoRa
                  ? "border-none"
                  : "border-none opacity-50"
              }
            >
              <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
                <div className="flex items-center gap-2">
                  <span>{t("Wartung IoT QPM")}</span>
                  {!visibility.maintenance.aLoRa ? (
                    <InfoIconTooltip text={hiddenBySettingText} />
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <IntNumberInput
                  className="ml-auto w-20"
                  value={offer.maintenanceResult.qpmIoTAddon.amountOfMeasures}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.qpmIoTAddon.costPerMeasure}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.qpmIoTAddon.totalCost}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
            </TableRow>
          )}
          {availability.maintenance.scb && (
            <TableRow
              className={
                visibility.maintenance.scb
                  ? "border-none"
                  : "border-none opacity-50"
              }
            >
              <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
                <div className="flex items-center gap-2">
                  <span>{t("Service SbD Monitore")}</span>
                  {!visibility.maintenance.scb ? (
                    <InfoIconTooltip text={hiddenBySettingText} />
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <IntNumberInput
                  className="ml-auto w-20"
                  value={offer.maintenanceResult.scb.amountOfMeasures}
                  disabled={disabled || !visibility.maintenance.scb}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.scb.costPerMeasure}
                  disabled={disabled || !visibility.maintenance.scb}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.scb.totalCost}
                  disabled={disabled || !visibility.maintenance.scb}
                />
              </TableCell>
            </TableRow>
          )}
          {availability.maintenance.aLoRa && (
            <TableRow
              className={
                visibility.maintenance.aLoRa
                  ? "border-none"
                  : "border-none opacity-50"
              }
            >
              <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
                <div className="flex items-center gap-2">
                  <span>{t("Funktionsprüfung ALoRa")}</span>
                  {!visibility.maintenance.aLoRa ? (
                    <InfoIconTooltip text={hiddenBySettingText} />
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <IntNumberInput
                  className="ml-auto w-20"
                  value={offer.maintenanceResult.aLoRa.amountOfMeasures}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.aLoRa.costPerMeasure}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
              <TableCell className="px-2 py-2.5 text-end">
                <FloatNumberInput
                  className="ml-auto w-32"
                  value={offer.maintenanceResult.aLoRa.totalCost}
                  disabled={disabled || !visibility.maintenance.aLoRa}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
